import { useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/Button";
import { Field, Input, PasswordInput } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { AuthAlreadyRedirect, AuthCtaButton, AuthLayout } from "@/components/layout/AuthLayout";
import { useI18n } from "@/hooks/useI18n";
import { ApiError } from "@/lib/api";
import { isGoogleSignInEnabled, signInWithGoogle } from "@/lib/firebase";
import { useAuth } from "@/providers/AuthProvider";
import { googleErrorMessage, googlePopupMessage, mapLoginError } from "./authErrors";

/** Served from `apps/web/public`, so it survives the Vite asset pipeline. */
const GOOGLE_LOGO_URL = `${import.meta.env.BASE_URL}google-logo.png`;

export function LoginPage() {
    const { login, loginWithGoogle } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();
    const prefillEmail = (location.state as { email?: string } | null)?.email ?? "";
    const [identifier, setIdentifier] = useState(prefillEmail);
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [googleSubmitting, setGoogleSubmitting] = useState(false);

    const handleGoogle = async () => {
        setError(null);
        setNotice(null);
        setGoogleSubmitting(true);
        try {
            let credential;
            try {
                credential = await signInWithGoogle();
            } catch (caught) {
                const message = googlePopupMessage(caught, t);
                if (message) setError(message);
                return;
            }
            try {
                const profile = await loginWithGoogle(credential.idToken);
                if (!profile.emailVerifiedAt && profile.email) {
                    setNotice(t("auth.checkEmailCode", { email: profile.email }));
                    navigate("/verify-email", {
                        replace: true,
                        state: { email: profile.email },
                    });
                    return;
                }
                navigate("/plan", { replace: true });
            } catch (caught) {
                // Coaches are normally provisioned by the company, but an unknown
                // Google account can now finish signing up instead of hitting a
                // dead end — the token travels along so it gets linked.
                const unlinked =
                    caught instanceof ApiError &&
                    caught.status === 401 &&
                    /no coach account/i.test(caught.message);

                if (unlinked) {
                    navigate("/signup", {
                        replace: true,
                        state: {
                            email: credential.email ?? undefined,
                            fullName: credential.fullName ?? undefined,
                            googleIdToken: credential.idToken,
                        },
                    });
                    return;
                }

                setError(googleErrorMessage(caught, t));
            }
        } finally {
            setGoogleSubmitting(false);
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        setNotice(null);
        setSubmitting(true);
        try {
            await login(identifier.trim(), password);
            // OTP-dulu: backend menolak 403 sebelum verifikasi, jadi
            // sampai sini berarti akun aktif + verified → langsung /plan.
            navigate("/plan", { replace: true });
        } catch (caught) {
            const mapped = mapLoginError(caught, t);
            if (mapped.needsVerification) {
                // Backend sudah kirim ulang OTP saat menolak 403 — cukup
                // antar ke halaman OTP bawa email yang diketik user.
                const email = /@/.test(identifier.trim())
                    ? identifier.trim().toLowerCase()
                    : undefined;
                navigate("/verify-email", { replace: true, state: { email } });
                return;
            }
            setError(mapped.text);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <AuthAlreadyRedirect />
            <AuthLayout
                subtitle={t("auth.subtitle")}
                heroCta={
                    <>
                        <p className="app-text-primary text-lg font-semibold">
                            {t("auth.heroCta")}
                        </p>
                        <AuthCtaButton to="/signup">{t("auth.heroCtaButton")}</AuthCtaButton>
                    </>
                }
            >
                <form
                    onSubmit={handleSubmit}
                    className="app-content-card rounded-2xl border p-6 shadow-md"
                >
                    <div className="flex flex-col gap-4">
                        <Field label={t("auth.identifier")} required>
                            <Input
                                value={identifier}
                                onChange={(event) => setIdentifier(event.target.value)}
                                placeholder="coach@flexv.my.id"
                                autoComplete="username"
                                autoFocus
                                required
                            />
                        </Field>

                        <Field label={t("auth.password")} required>
                            <PasswordInput
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                placeholder="••••••••"
                                autoComplete="current-password"
                                required
                            />
                        </Field>

                        {error && <Alert tone="error">{error}</Alert>}
                        {notice && <Alert tone="info">{notice}</Alert>}

                        <Button type="submit" size="lg" loading={submitting} fullWidth>
                            {t("auth.signIn")}
                        </Button>

                        {isGoogleSignInEnabled && (
                            <>
                                <div className="flex items-center gap-3">
                                    <div className="app-shell-border flex-1 border-t" />
                                    <span className="app-text-soft text-xs uppercase">
                                        {t("auth.or")}
                                    </span>
                                    <div className="app-shell-border flex-1 border-t" />
                                </div>

                                <Button
                                    type="button"
                                    variant="outline"
                                    size="lg"
                                    fullWidth
                                    loading={googleSubmitting}
                                    disabled={submitting}
                                    onClick={handleGoogle}
                                    icon={
                                        googleSubmitting ? undefined : (
                                            <img
                                                src={GOOGLE_LOGO_URL}
                                                alt=""
                                                aria-hidden="true"
                                                className="h-5 w-5"
                                            />
                                        )
                                    }
                                >
                                    {t("auth.googleSignIn")}
                                </Button>
                            </>
                        )}
                    </div>
                </form>

                <p className="app-text-soft mt-6 text-center text-xs">
                    {t("auth.demoAccount")}: <span className="font-semibold">coach</span> /{" "}
                    <span className="font-semibold">coach123</span>
                </p>
            </AuthLayout>
        </>
    );
}
